"use client";

/**
 * What this person is doing here, and the market numbers that go with it
 * (GET /api/me/home, backend services/intent + services/home_feed).
 *
 * One read per page load, shared by every component that asks (the hub,
 * the public hero, the prompts). Signed out, identity is the persistent
 * client id; a visitor's own pick lives in localStorage and is sent as
 * ``goal`` (and copied onto the account the first time they sign in, if
 * the account has none yet).
 */
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";
import { getClientId } from "@/lib/client-id";
import { useAuth, type User } from "@/lib/auth-context";
import { hubBootPending, onHubBoot, startHubBoot, type HubBoot } from "@/lib/hub-boot";
import { VISITOR_GOAL_KEY } from "@/lib/visitor-goal";

export type Intent = "buy" | "rent" | "invest" | "sell" | "rent_out" | "agent" | "commercial" | "garage";
export const INTENTS: Intent[] = ["buy", "rent", "invest", "sell", "rent_out", "agent", "commercial", "garage"];
/** What a person can pick for themselves. "agent" is supported (an agent
 *  or developer account gets it from its account type) but not offered
 *  (owner, 2026-10-04: "we support it, but you cannot select it"). An
 *  office or an obyekt, and a garage or a parking place, are goals of
 *  their own (owner, 2026-10-21: "the goal should also be there"). */
export const SELECTABLE_INTENTS: Intent[] = ["buy", "rent", "invest", "sell", "rent_out", "commercial", "garage"];
export const isIntent = (v: unknown): v is Intent => INTENTS.includes(v as Intent);
/** The goals that are a listing category of their own: their market is
 *  that category's listings, never a home statistic. */
export type SeparateIntent = "commercial" | "garage";
export const isSeparateIntent = (v: unknown): v is SeparateIntent => v === "commercial" || v === "garage";

export interface IntentPayload {
  effective: Intent | null;
  explicit: Intent | null;
  inferred: Intent | null;
  confidence: number;
  evidence: number;
  shares: Partial<Record<Intent, number>>;
  secondary: Intent | null;
  drift: { from: Intent; to: Intent; share: number } | null;
  profile: {
    params: Record<string, string | string[]>;
    location_names: string[];
    samples: number;
    /** The person said it (the goal picker, an accepted suggestion),
     *  over what they browsed. */
    stated?: boolean;
  } | null;
}

/** What a person says with their goal (users.goal_params; backend
 *  services/goal_params). */
export interface GoalParams {
  location_ids?: string[];
  location_names?: string[];
  price_max?: number;
  rooms?: number[];
  category?: "apartment" | "house" | "land" | "commercial" | "garage";
  /** The commercial and garage goals: the kinds (office, obyekt; garage,
   *  parking) and the side (true to rent, false to buy, absent both). */
  property_kinds?: string[];
  leased?: boolean;
  property_id?: number;
  residence?: string;
  source?: "picker" | "suggestion";
}

/** The alert form's prefill for the person's own search (the roadmap's
 *  alert step, the alerts widget's first alert): the goal's side and
 *  kind, so an office seeker's alert is an office alert. */
export function profileAlertBlob(eff: Intent | null, params: Record<string, string | string[]> | null | undefined): Record<string, unknown> {
  const list = (v: string | string[] | undefined) => ([] as string[]).concat(v ?? []);
  if (isSeparateIntent(eff)) {
    const blob: Record<string, unknown> = { category: eff };
    if (params?.category === eff) {
      if (params.leased === "true") blob.leased = true;
      else if (params.leased === "false") blob.leased = false;
      if (list(params.property_kind).length) blob.property_kinds = list(params.property_kind);
      if (params.location_ids) blob.location_ids = list(params.location_ids);
      if (params.price_max) blob.price_max = Number(params.price_max);
    }
    return blob;
  }
  const leased = eff === "rent" || eff === "rent_out";
  const blob: Record<string, unknown> = { leased };
  if (params && (params.leased === "true") === leased) {
    if (params.location_ids) blob.location_ids = list(params.location_ids);
    if (params.rooms) blob.room_counts = list(params.rooms).map(Number);
    if (params.price_max) blob.price_max = Number(params.price_max);
    // A house or a plot buyer's alert is for houses or plots.
    if (params.category === "house" || params.category === "land") blob.listing_kind = [params.category];
  }
  return blob;
}

export interface TrendPoint { week: string; median_ppm: number | null; median_price: number | null; count: number }
export interface AreaTrend {
  scope: "area" | "city";
  leased: boolean;
  series: TrendPoint[];
  now_ppm: number;
  change_pct: number | null;
  /** Median asking price, the last 4 weeks (a month's rent for rentals). */
  now_price: number | null;
  price_change_pct: number | null;
  listings: number;
  /** City scope only: the Bakı benchmark behind now_ppm (active listings) and its date. */
  benchmark_listings?: number;
  as_of?: string;
}
export interface DealFlow {
  series: { day: string; count: number }[];
  /** The same deals by week, 12 weeks (deals arrive with the weekly scrape). */
  weeks?: { week: string; count: number }[];
  today: number;
  today_exceptional: number;
  active: number;
}

export interface PersonalHome {
  intent: IntentPayload;
  trend: AreaTrend | null;
  trend_place: string | null;
  /** The searched places' names, in the order of the search's ids. */
  place_names?: string[];
  /** The rayon of the person's area, for the hub's city map. */
  district: string | null;
  deals: DealFlow | null;
  fresh: { total: number; day: number; week: number } | null;
  home: { property_id: number; rooms: number | null; location_id: string; property_type?: string | null } | null;
  /** The Bakı benchmark for the person's deal side (services/benchmark). */
  baku?: { median_ppm: number | null; listings: number; as_of: string } | null;
}

const GOAL_KEY = VISITOR_GOAL_KEY;
// The last effective intent, so a returning visitor's page adapts on the
// first paint after hydration instead of when /me/home answers.
const LAST_KEY = "menzil_intent_last";

export function getCachedIntent(): Intent | null {
  try {
    const v = localStorage.getItem(LAST_KEY);
    return isIntent(v) ? v : null;
  } catch {
    return null;
  }
}

export function getVisitorGoal(): Intent | null {
  try {
    const v = localStorage.getItem(GOAL_KEY);
    return isIntent(v) ? v : null;
  } catch {
    return null;
  }
}

function setVisitorGoal(g: Intent | null): void {
  try {
    if (g) localStorage.setItem(GOAL_KEY, g);
    else localStorage.removeItem(GOAL_KEY);
  } catch {
    /* private mode: the pick lasts this page */
  }
}

// One in-flight read per identity; listeners re-render on refresh.
const store: {
  key: string | null;
  data: PersonalHome | null;
  pending: Promise<PersonalHome | null> | null;
  /** A goal PATCH is in flight: hold reads until it lands. */
  choosing: boolean;
  listeners: Set<() => void>;
} = { key: null, data: null, pending: null, choosing: false, listeners: new Set() };

function notify() {
  store.listeners.forEach((l) => l());
}

// What the hub renders when the read fails: the neutral page (the
// question, the city), never a skeleton that stays.
const EMPTY: PersonalHome = {
  intent: { effective: null, explicit: null, inferred: null, confidence: 0, evidence: 0, shares: {}, secondary: null, drift: null, profile: null },
  trend: null, trend_place: null, district: null, deals: null, fresh: null, home: null,
};

function settle(key: string, d: PersonalHome) {
  if (store.key === key) {
    store.data = d;
    notify();
  }
}

function remember(d: PersonalHome) {
  try {
    if (d.intent?.effective) localStorage.setItem(LAST_KEY, d.intent.effective);
    else localStorage.removeItem(LAST_KEY);
  } catch {
    /* private mode */
  }
}

async function load(key: string, goal: Intent | null): Promise<PersonalHome | null> {
  const url = new URL(`${API_BASE}/me/home`, window.location.origin);
  if (goal) url.searchParams.set("goal", goal);
  try {
    const r = await fetch(url.toString(), {
      credentials: "include",
      headers: { "X-Client-Id": getClientId() },
    });
    if (!r.ok) { settle(key, EMPTY); return null; }
    const d = (await r.json()) as PersonalHome;
    remember(d);
    settle(key, d);
    return d;
  } catch {
    settle(key, EMPTY);
    return null;
  }
}

/** The store key the hub's bootstrap payload answers. */
const bootKey = (b: HubBoot) => `${b.user_id}|${b.goal ?? ""}`;

function ensure(key: string, goal: Intent | null, force = false) {
  if (!force && store.key === key && (store.data || store.pending)) return;
  // A different person: drop theirs. The same person with a new goal
  // keeps the old payload on screen until the new one lands.
  if (store.key?.split("|")[0] !== key.split("|")[0]) store.data = null;
  store.key = key;
  // Signed in on the hub, its one-read bootstrap (lib/hub-boot) carries
  // this payload: wait for it instead of asking a second time, and read
  // /me/home only when it brings none for this account and goal.
  const boot = !force && !key.startsWith("anon|") ? hubBootPending() : null;
  const read = boot
    ? boot.then((b) => {
        if (!b || bootKey(b) !== key) return load(key, goal);
        remember(b.home);
        settle(key, b.home);
        return b.home;
      })
    : load(key, goal);
  store.pending = read.finally(() => { store.pending = null; });
}

// The bootstrap landing before anyone asked (the account not known yet):
// its payload is already the store's, so the hub's first render has it.
onHubBoot((b) => {
  const key = bootKey(b);
  if (store.choosing || (store.key != null && store.key !== key)) return;
  store.key = key;
  store.data = b.home;
  remember(b.home);
  notify();
});

/** Starts the hub's one-read bootstrap, beside /auth/me while the account
 *  is still being checked (``uid`` null), unless this account's payload
 *  is here already (a return to the hub reads nothing new). */
export function primeHub(uid: string | null): void {
  if (typeof window === "undefined") return;
  if (uid && store.key?.startsWith(`${uid}|`) && store.data) return;
  void startHubBoot();
}

/**
 * ``choose`` sets the goal: on the account when signed in (PATCH
 * /auth/me/goal), in the browser otherwise. Either way the payload is
 * re-read, so the page adapts at once.
 */
export function usePersonal() {
  const { user, loading, setUser } = useAuth();
  const [, bump] = useState(0);
  // Signed in, the account's own goal is the key (a browser pick that is
  // about to be copied onto it must not stand in, or the copy would not
  // re-read the payload).
  const key = loading ? null : user ? `${user.id}|${user.goal ?? ""}` : `anon|${getVisitorGoal() ?? ""}`;

  useEffect(() => {
    const l = () => bump((v) => v + 1);
    store.listeners.add(l);
    return () => { store.listeners.delete(l); };
  }, []);

  useEffect(() => {
    if (key == null || store.choosing) return;
    ensure(key, user ? null : getVisitorGoal());
  }, [key, user]);

  // A visitor's pick carries over to a new account that has no goal.
  useEffect(() => {
    if (!user || user.goal) return;
    const g = getVisitorGoal();
    if (!g) return;
    void fetch(`${API_BASE}/auth/me/goal`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ goal: g }),
    }).then((r) => { if (r.ok) setUser({ ...user, goal: g } as User); }).catch(() => {});
  }, [user, setUser]);

  // ``params``: what the person said with the goal (undefined keeps the
  // stored ones while the goal stays, null clears them).
  const choose = useCallback(async (g: Intent, params?: GoalParams | null, merge = false) => {
    if (user) {
      const prev = user;
      store.choosing = true;
      setUser({ ...user, goal: g } as User);
      try {
        const r = await fetch(`${API_BASE}/auth/me/goal`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params === undefined ? { goal: g } : { goal: g, params, ...(merge ? { merge: true } : {}) }),
        });
        if (!r.ok) { setUser(prev); return false; }
        // Re-read only once the account carries the new goal.
        ensure(`${user.id}|${g}`, null, true);
      } catch {
        setUser(prev);
        return false;
      } finally {
        store.choosing = false;
      }
    } else {
      setVisitorGoal(g);
      ensure(`anon|${g}`, g, true);
      bump((v) => v + 1);
    }
    return true;
  }, [user, setUser]);

  const data = key != null && store.key?.split("|")[0] === key.split("|")[0] ? store.data : null;
  // The old payload stays on screen while the new role's loads; the
  // hub shows a progress line instead of re-flowing (owner, 2026-10-04:
  // "I don't like how content jumps").
  const switching = !!store.pending && data != null;
  return { data, ready: data != null, choose, switching };
}

/** The payload when another caller (the hub) has loaded it; never
 *  fetches, so a widget shared with other pages can read the stated
 *  params without a /me/home read of its own. */
export function usePersonalPeek(): PersonalHome | null {
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((v) => v + 1);
    store.listeners.add(l);
    return () => { store.listeners.delete(l); };
  }, []);
  return store.data;
}

// ── The trend for a search the person just changed ─────────────────
// GET /api/me/trend for the hero's live filters (kind, place, rooms),
// cached by its query so the hero and the pulse share one request.
const TREND_CACHE = new Map<string, Promise<{ trend: AreaTrend | null; trend_place: string | null }>>();

export function trendQuery(params: Record<string, string | string[]> | null): string | null {
  if (!params) return null;
  // An office's or a garage's market has no AZN/m² trend of its own here,
  // and a flat's must never stand in for it.
  if (isSeparateIntent(params.category)) return null;
  const u = new URLSearchParams();
  u.set("leased", params.leased === "true" ? "true" : "false");
  const cat = params.category;
  u.set("category", cat === "house" || cat === "land" ? cat : "apartment");
  for (const id of ([] as string[]).concat(params.location_ids ?? [])) u.append("location_ids", id);
  for (const r of ([] as string[]).concat(params.rooms ?? [])) u.append("rooms", r);
  return u.toString();
}

export function useAreaTrend(query: string | null): { trend: AreaTrend | null; trend_place: string | null } | null {
  const [out, setOut] = useState<{ q: string; v: { trend: AreaTrend | null; trend_place: string | null } } | null>(null);
  useEffect(() => {
    if (!query) return;
    let alive = true;
    let p = TREND_CACHE.get(query);
    if (!p) {
      p = fetch(`${API_BASE}/me/trend?${query}`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { trend: null, trend_place: null }))
        .catch(() => ({ trend: null, trend_place: null }));
      TREND_CACHE.set(query, p);
      if (TREND_CACHE.size > 50) TREND_CACHE.delete(TREND_CACHE.keys().next().value as string);
    }
    p.then((v) => { if (alive) setOut({ q: query, v }); });
    return () => { alive = false; };
  }, [query]);
  return query && out?.q === query ? out.v : null;
}
